import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TiptapDoc } from '@odo/shared-ui';
import type { VirtualAppointmentCancellation } from '../../../services/virtualAppointments';
import type {
    VirtualAppointmentOverviewPerson,
    VirtualAppointmentOverviewItem
} from '../../../services/virtualAppointmentsOverview';
import {
    isVirtualAppointmentUnavailable,
    useVirtualAppointmentOverviewDetail
} from '../../../hooks/useVirtualAppointmentsOverview';
import { useToast } from '../../../providers/ToastProvider';
import { VIRTUAL_APPOINTMENT_DETAIL_PARAM } from '../appointments/appointmentsOverlayParams';
import type { DescriptionState } from './VirtualAppointmentOverviewDetail';
import { useResolvedDescriptionDoc } from './useResolvedDescriptionDoc';

export interface OverviewSelection {
    selectedId: string | null;
    /** The list row when loaded, else the fetched detail (a deep link to another tab or page). */
    selectedItem: VirtualAppointmentOverviewItem | null;
    failed: boolean;
    descriptionDoc: TiptapDoc | null;
    /** Only the detail endpoint knows it; null until that loads. */
    createdBy: VirtualAppointmentOverviewPerson | null;
    resolvedBy: VirtualAppointmentOverviewPerson | null;
    cancellation: VirtualAppointmentCancellation | null;
    descriptionState: DescriptionState;
    retry: () => void;
    toggle: (item: VirtualAppointmentOverviewItem) => void;
    close: () => void;
}

// ?va=<id> is the source of truth: pushed on open so Back closes the pane, replaced on close.
export function useOverviewSelection(
    items: readonly VirtualAppointmentOverviewItem[]
): OverviewSelection {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get(VIRTUAL_APPOINTMENT_DETAIL_PARAM);
    const { showToast } = useToast();
    const detail = useVirtualAppointmentOverviewDetail(selectedId);

    const close = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(VIRTUAL_APPOINTMENT_DETAIL_PARAM);
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const toggle = useCallback(
        (item: VirtualAppointmentOverviewItem) => {
            if (item.id === selectedId) {
                close();
                return;
            }
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(VIRTUAL_APPOINTMENT_DETAIL_PARAM, item.id);
                return next;
            });
        },
        [selectedId, close, setSearchParams]
    );

    useEffect(() => {
        if (!selectedId || !detail.isError || !isVirtualAppointmentUnavailable(detail.error))
            return;
        showToast('Virtual appointment not found', 'error');
        close();
    }, [selectedId, detail.isError, detail.error, showToast, close]);

    const selectedItem = useMemo(
        () => items.find((item) => item.id === selectedId) ?? detail.data ?? null,
        [items, selectedId, detail.data]
    );
    const descriptionDoc = useResolvedDescriptionDoc(
        selectedItem?.patient.id ?? '',
        detail.data?.descriptionDoc ?? null
    );
    const { refetch } = detail;
    const retry = useCallback(() => void refetch(), [refetch]);

    return {
        selectedId,
        selectedItem,
        failed: detail.isError,
        descriptionDoc,
        createdBy: detail.data?.createdBy ?? null,
        resolvedBy: detail.data?.resolvedBy ?? null,
        cancellation: detail.data?.cancellation ?? null,
        descriptionState: detail.isPending ? 'loading' : detail.isError ? 'error' : 'ready',
        retry,
        toggle,
        close
    };
}
